import { getUrlFromLink } from 'lib/helpers'
import { urlForImage } from 'lib/sanity.image'
import { motion } from 'motion/react'

import Link from 'next/link'
import Image from 'next/image'
import { Button } from 'components/Global/Button'
import { CloseIcon } from 'components/Global/CloseIcon'
import { PortableText } from '@portabletext/react'
import { DonationTag } from 'components/Global/DonationTag'

export const ProductMarketingOverlay = ({ tabs, activeTab, setActiveTab }) => {
  return (
    <div className="h-screen-dynamic fixed inset-0 z-[55] flex w-full justify-center overflow-x-hidden overflow-y-scroll overscroll-contain p-14">
      <motion.div
        className="fixed inset-0 z-[-1] h-full w-full bg-blackTransparent backdrop-blur-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setActiveTab(null)}
        transition={{
          duration: 0.4,
          ease: 'easeInOut',
        }}
      ></motion.div>
      <motion.div
        className="h-full w-full max-w-[700px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.4,
          ease: 'easeInOut',
        }}
      >
        <div className="w-full py-60">
          <div className="sticky top-60 z-[9] flex w-full justify-between pb-20 text-11 text-darkGray">
            <div className="flex gap-4 rounded-8 bg-white p-4 shadow-sm">
              {tabs.map((item) => {
                if (!item.details) return null
                return (
                  <Button
                    key={item.label}
                    active={activeTab === item.label}
                    onClick={() => setActiveTab(item.label)}
                    className={`py-8 leading-100 md:py-10 md:hover:bg-[#DEDEDE] ${
                      activeTab === item.label ? 'bg-[#DEDEDE]' : ''
                    }`}
                  >
                    {item.label}
                  </Button>
                )
              })}
            </div>
            <div className="rounded-8 bg-white p-4 text-11 shadow-sm">
              <Button
                className="h-full px-8 py-8 md:px-10 md:py-10 md:hover:!bg-[#DEDEDE]"
                onClick={() => setActiveTab(null)}
              >
                <CloseIcon />
              </Button>
            </div>
          </div>
          <div className="rounded-12 bg-white px-20 py-30">
            {tabs.map((item) => {
              if (item.label !== activeTab) return null
              if (item.type === 'factory')
                return <FactoryCard key={item.label} {...item.details} />
              if (item.type === 'impact')
                return <ImpactCard key={item.label} {...item.details} />
              if (item.type === 'wornByYou')
                return <WornByYouCard key={item.label} {...item.details} />
              return null
            })}
          </div>
        </div>
      </motion.div>
    </div>
  )
}

const ImpactCard = ({ title, subtitle, description, amount, image }) => {
  return (
    <div>
      <div className="flex flex-col gap-4 text-12 leading-100 md:text-20">
        <div className="text-midGray ">{title}</div>
        <div className="text-gray">{subtitle}</div>
      </div>
      <div className="max-w-[400px] pt-30 text-12 leading-120 text-gray md:text-14">
        <PortableText value={description} />
      </div>
      <div
        className="relative mt-30 w-full overflow-hidden rounded-12"
        style={{ aspectRatio: image.aspect ?? '1/1' }}
      >
        <DonationTag
          amount={amount}
          className="absolute left-20 top-28 z-[2]"
        />
        <Image
          src={urlForImage(image).width(800).quality(90).auto('format').url()}
          fill
          alt=""
          style={{ objectFit: 'cover' }}
          placeholder="blur"
          blurDataURL={image.lqip}
        />
      </div>
    </div>
  )
}

const FactoryCard = ({ title, body, image, tag, link }) => {
  const url = getUrlFromLink(link)
  return (
    <div>
      <div className=" text-12 text-midGray md:text-20">{title}</div>
      <div className="max-w-[400px] pt-30 text-12 leading-120 text-gray md:text-14">
        <PortableText value={body} />
      </div>
      <div
        className="relative mt-30 w-full overflow-hidden rounded-12"
        style={{ aspectRatio: image.aspect ?? '1/1' }}
      >
        <Button
          as="link"
          href={url}
          className="absolute left-20 top-28 z-[1] bg-yellow px-16"
        >
          {tag}
        </Button>
        <Image
          src={urlForImage(image).width(800).quality(90).auto('format').url()}
          fill
          alt=""
          style={{ objectFit: 'cover' }}
          placeholder="blur"
          blurDataURL={image.lqip}
        />
      </div>
      <Link
        scroll={false}
        href={url}
        className="mt-12 w-full border-t-1 border-borderGray pt-20 text-center text-darkGray underline opacity-50 transition-opacity duration-300 md:hover:opacity-100"
      >
        {link.label}
      </Link>
    </div>
  )
}

const WornByYouCard = ({ image, items }) => {
  return (
    <div>
      <div className="pb-30 text-14 leading-100 md:text-20">
        <div className="text-midGray">Worn by you</div>
        <div className="text-gray">Tag us @chnge to be featured</div>
      </div>
      <div className="grid grid-cols-2 gap-12 md:grid-cols-3">
        {items.map((item) => {
          return (
            <div key={item._key}>
              <div className="relative aspect-[2/3.2] overflow-hidden rounded-4">
                {item.image ? (
                  <Image
                    src={urlForImage(item.image)
                      .width(800)
                      .quality(90)
                      .auto('format')
                      .url()}
                    fill
                    alt=""
                    style={{ objectFit: 'cover' }}
                    placeholder="blur"
                    blurDataURL={image.lqip}
                  />
                ) : null}
                {item.video ? (
                  <video
                    src={item.video.url}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : null}
              </div>
              <div className="pt-6 text-10 leading-120 text-gray">
                @{item.handle}, {item.platform}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
